"use client";

import React, { useEffect, useRef, useState } from "react";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const milestones = [{
  id: "m2",
  year: "2005",
  title: "Global Scale",
  description: "We expanded our reach globally, managing multi-billion dollar digital transformations for industry leaders across three continents.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606552/img/milestone_2005_enterprise.png"
}, {
  id: "m3",
  year: "2018",
  title: "Operational Mastery",
  description: "Pioneered proprietary process optimization frameworks that saved our enterprise clients over $2B in cumulative operational overhead.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606552/img/milestone_2018_efficiency.png"
}, {
  id: "m4",
  year: "2024",
  title: "The AI Frontier",
  description: "Bringing 40 years of elite consulting wisdom to SMEs through cutting-edge AI integration and agentic process automation solutions.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606552/img/milestone_2024_ai.png"
}];
interface MilestoneItemProps {
  item: (typeof milestones)[0];
  index: number;
  "data-index": number;
}
function MilestoneItem({
  item,
  index,
  "data-index": dataIndex
}: MilestoneItemProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="69605" ref={ref} data-index={dataIndex} className={cn("relative flex flex-col md:flex-row items-center justify-between mb-24 last:mb-0 group transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16")}>
      {}
      <div className="absolute left-6 md:left-1/2 w-5 h-5 rounded-full bg-secondary ring-4 ring-background md:-translate-x-1/2 z-10 transition-transform duration-500 group-hover:scale-125 shadow-[0_0_20px_rgba(37,99,235,0.4)]" />

      {}
      <div className="md:hidden w-full pl-16">
        <div className="text-secondary font-mono font-bold text-2xl mb-4 tracking-tighter">
          {item.year}
        </div>
        <Card className="bg-card/60 backdrop-blur-md border-border/50 overflow-hidden shadow-xl">
          {item.image && <div className="relative h-48 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent z-10" />
              <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>}
          <CardHeader className="relative z-20 -mt-8">
            <CardTitle className="text-xl font-bold text-primary">{item.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-base text-slate-600 leading-relaxed font-medium">
              {item.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>

      {}
      <div className="hidden md:flex w-full items-center justify-between">
        {}
        <div className={cn("w-[calc(50%-4rem)] flex", isEven ? "justify-end" : "justify-start order-2")}>
          {isEven ? <Card className="w-full max-w-lg bg-card/40 backdrop-blur-sm border-border/40 overflow-hidden shadow-2xl hover:border-secondary/40 transition-all duration-500 text-right">
              {item.image && <div className="relative h-64 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/10 to-transparent z-10" />
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 group-hover:scale-110" />
                </div>}
              <CardHeader className="relative z-20 -mt-12">
                <CardTitle className="text-2xl font-bold text-primary">{item.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-slate-600 leading-relaxed font-medium">
                  {item.description}
                </CardDescription>
              </CardContent>
            </Card> : <div className="w-full max-w-lg flex items-center justify-start pl-8">
              <div className="relative">
                <span className="text-8xl font-black text-secondary/10 tracking-tighter select-none font-sans">
                  {item.year}
                </span>
                <span className="absolute top-1/2 left-8 -translate-y-1/2 text-3xl font-mono font-bold text-secondary">
                  {item.year}
                </span>
              </div>
            </div>}
        </div>

        {}
        <div className={cn("w-[calc(50%-4rem)] flex", isEven ? "justify-start order-2" : "justify-end")}>
          {isEven ? <div className="w-full max-w-lg flex items-center justify-end pr-8">
              <div className="relative">
                <span className="text-8xl font-black text-secondary/10 tracking-tighter select-none font-sans">
                  {item.year}
                </span>
                <span className="absolute top-1/2 right-8 -translate-y-1/2 text-3xl font-mono font-bold text-secondary">
                  {item.year}
                </span>
              </div>
            </div> : <Card className="w-full max-w-lg bg-card/40 backdrop-blur-sm border-border/40 overflow-hidden shadow-2xl hover:border-secondary/40 transition-all duration-500 text-left">
              {item.image && <div className="relative h-64 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/10 to-transparent z-10" />
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 group-hover:scale-110" />
                </div>}
              <CardHeader className="relative z-20 -mt-12">
                <CardTitle className="text-2xl font-bold text-primary">{item.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-slate-600 leading-relaxed font-medium">
                  {item.description}
                </CardDescription>
              </CardContent>
            </Card>}
        </div>
      </div>
    </div>;
}
export default function OurMilestone() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.5
  });
  return <section id="a-legacy-of-transformation" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-secondary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerRef} className={cn("text-center max-w-3xl mx-auto mb-24 transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <h2 className="text-sm font-mono text-secondary tracking-widest uppercase mb-4 font-semibold">
            Our Story
          </h2>
          <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-6 text-primary">
            The Evolution of <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-secondary to-primary">
              FocusPath Consulting
            </span>
          </h3>
          <p className="text-lg text-slate-600 leading-relaxed font-medium">From four decades of global enterprise excellence to pioneering technology driven growth. The FocusPath idea started back when two childhood friends started building websites for small businesses during the internet boom.&nbsp; Both went on to have succcessful consulting careers and have re-united to bring that experience to SMBs.</p>
        </div>

        {}
        <div className="relative max-w-6xl mx-auto">
          {}
          <Separator orientation="vertical" className="absolute left-6 md:left-1/2 top-4 bottom-4 md:-translate-x-1/2 bg-gradient-to-b from-transparent via-secondary/30 to-transparent w-px" />

          {}
          
        </div>
      </div>
    </section>;
}